import { MagpieConnection } from 'magpie-lib/connection';
import { useCallback, useEffect } from 'react';
import { ncRemoteOperations, ncRemoteOperationsReplies } from '../../../common/messages/nav-operation.message';
import { navComponentCommsTopicDef } from '../../../common/topics';
import { useNavComponentsManager } from '../useNavComponentsManager';
export function useNavigationComponentReceiver() {
  const {
    configSpec,
    getComponentState,
    setComponentState,
    getComponentProps,
    setComponentProps,
    setIsLoaded,
    waitUntilReady,
    handleInvokeFunction
  } = useNavComponentsManager();
  const isCorrectPlatform = useCallback((message, context) => 'jasmine' in window || message.payload.platformName === context.appName, []);
  useEffect(() => {
    const topic = MagpieConnection.get().useTopic(navComponentCommsTopicDef);
    const unsubscribeGetState = topic.on(ncRemoteOperations.getState.getType(), (message, {
      reply
    }) => {
      const {
        name
      } = message.payload;
      const componentState = getComponentState(name);
      const replyMessage = ncRemoteOperationsReplies.getStateReply.create(componentState);
      reply(replyMessage);
    }, {
      filter: isCorrectPlatform
    });
    const unsubscribeSetProps = topic.on(ncRemoteOperations.setProps.getType(), message => {
      const {
        name,
        value
      } = message.payload;
      setComponentProps(configSpec[name], otherProps => Object.assign({}, otherProps, value));
    }, {
      filter: isCorrectPlatform
    });
    const unsubscribeSetLoaded = topic.on(ncRemoteOperations.setIsLoaded.getType(), (message, {
      reply
    }) => {
      const {
        name,
        value
      } = message.payload;
      setIsLoaded(name, value);
      waitUntilReady(name).then(() => {
        reply(ncRemoteOperationsReplies.setIsLoadedReply.create());
      }).catch(error => {
        setTimeout(() => {
          throw error;
        }, 0);
      });
    }, {
      filter: isCorrectPlatform
    });
    const unsubscribeSetOpened = topic.on(ncRemoteOperations.setIsOpen.getType(), message => {
      const {
        name,
        value
      } = message.payload;
      setComponentState(name, currentState => Object.assign({}, currentState, {
        isOpen: value,
        isLoaded: true
      }));
    }, {
      filter: isCorrectPlatform
    });
    const unsubscribeGetProps = topic.on(ncRemoteOperations.getProps.getType(), (message, {
      reply
    }) => {
      const {
        name
      } = message.payload;
      const componentProps = getComponentProps(configSpec[name]);
      const replyMessage = ncRemoteOperationsReplies.getPropsReply.create(componentProps);
      reply(replyMessage);
    }, {
      filter: isCorrectPlatform
    });
    const unsubscribeInvokeFunction = topic.on(ncRemoteOperations.invokeFunction.getType(), (message, {
      reply
    }) => {
      const {
        name,
        fnName,
        args
      } = message.payload;
      const componentDef = configSpec[name];
      handleInvokeFunction(componentDef, fnName, args).then(() => {
        reply(ncRemoteOperationsReplies.invokeFunctionReply.create());
      }).catch(error => {
        setTimeout(() => {
          throw error;
        }, 0);
      });
    }, {
      filter: isCorrectPlatform
    });
    return () => {
      unsubscribeGetState();
      unsubscribeSetProps();
      unsubscribeSetLoaded();
      unsubscribeSetOpened();
      unsubscribeGetProps();
      unsubscribeInvokeFunction();
    };
  }, [configSpec, getComponentProps, getComponentState, setComponentProps, setComponentState, setIsLoaded, waitUntilReady, isCorrectPlatform, handleInvokeFunction]);
}